import React from "react";
import { Box, Button, Container, Typography, styled, keyframes } from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import Logo from "../assests/images/component/logo.png";

const pulse = keyframes`
  0%, 100% { opacity: 0.55; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.6); }
`;

const Page = styled(Box)({
  minHeight: "100vh",
  backgroundColor: "#0d1620",
  color: "#fff",
  display: "flex",
  flexDirection: "column",
  position: "relative",
  overflow: "hidden",
});

const Grid = styled(Box)({
  position: "absolute",
  inset: 0,
  backgroundImage:
    "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
  backgroundSize: "64px 64px",
  maskImage:
    "radial-gradient(ellipse 60% 60% at 50% 50%, #000 30%, transparent 80%)",
  WebkitMaskImage:
    "radial-gradient(ellipse 60% 60% at 50% 50%, #000 30%, transparent 80%)",
  pointerEvents: "none",
});

const TopBar = styled(Box)(({ theme }) => ({
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(3, 5),
  borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
  [theme.breakpoints.down("sm")]: { padding: theme.spacing(2.5, 3) },
}));

const StatusPill = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  gap: 10,
  padding: "6px 14px",
  borderRadius: 999,
  border: "1px solid rgba(255, 255, 255, 0.12)",
  backgroundColor: "rgba(255, 255, 255, 0.04)",
  fontSize: "0.78rem",
  fontWeight: 500,
  letterSpacing: "0.04em",
  color: "rgba(255, 255, 255, 0.78)",
});

const StatusDot = styled("span")({
  position: "relative",
  width: 8,
  height: 8,
  borderRadius: "50%",
  backgroundColor: "#f59e0b",
  "&::after": {
    content: '""',
    position: "absolute",
    inset: -3,
    borderRadius: "50%",
    backgroundColor: "#f59e0b",
    opacity: 0.4,
    animation: `${pulse} 2.4s ease-out infinite`,
  },
});

const Main = styled(Container)(({ theme }) => ({
  position: "relative",
  flex: 1,
  display: "flex",
  alignItems: "center",
  paddingTop: theme.spacing(8),
  paddingBottom: theme.spacing(8),
}));

const Label = styled(Typography)({
  fontSize: "0.78rem",
  fontWeight: 600,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  color: "#7ec1e2",
  marginBottom: 22,
});

const Title = styled(Typography)(({ theme }) => ({
  fontSize: "4.5rem",
  fontWeight: 700,
  lineHeight: 1.04,
  letterSpacing: "-0.025em",
  color: "#fff",
  marginBottom: theme.spacing(3),
  maxWidth: 880,
  [theme.breakpoints.down("md")]: { fontSize: "3.2rem" },
  [theme.breakpoints.down("sm")]: { fontSize: "2.2rem" },
}));

const Lead = styled(Typography)(({ theme }) => ({
  fontSize: "1.15rem",
  lineHeight: 1.65,
  color: "rgba(255, 255, 255, 0.7)",
  marginBottom: theme.spacing(5),
  maxWidth: 620,
  [theme.breakpoints.down("sm")]: { fontSize: "1rem" },
}));

const PrimaryButton = styled(Button)({
  padding: "13px 26px",
  borderRadius: 8,
  fontWeight: 600,
  fontSize: "0.95rem",
  textTransform: "none",
  backgroundColor: "#2f81ad",
  color: "#fff",
  boxShadow: "none",
  "&:hover": { backgroundColor: "#1d648c", boxShadow: "none" },
  transition: "background-color 0.2s ease",
});

const InfoRow = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(7),
  paddingTop: theme.spacing(4),
  borderTop: "1px solid rgba(255, 255, 255, 0.08)",
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: theme.spacing(4),
  maxWidth: 720,
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
    gap: theme.spacing(3),
  },
}));

const InfoLabel = styled(Typography)({
  fontSize: "0.72rem",
  fontWeight: 600,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: "rgba(255, 255, 255, 0.42)",
  marginBottom: 6,
});

const InfoValue = styled(Typography)({
  fontSize: "0.95rem",
  fontWeight: 500,
  color: "rgba(255, 255, 255, 0.85)",
  lineHeight: 1.5,
});

const Footer = styled(Box)(({ theme }) => ({
  position: "relative",
  padding: theme.spacing(3, 5),
  borderTop: "1px solid rgba(255, 255, 255, 0.06)",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  fontSize: "0.82rem",
  color: "rgba(255, 255, 255, 0.5)",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2.5, 3),
    flexDirection: "column",
    gap: 8,
  },
}));

const UnderConstruction: React.FC = () => {
  return (
    <Page>
      <Grid aria-hidden="true" />

      <TopBar>
        <img src={Logo} alt="Smart IT Innovation" style={{ height: 32 }} />
        <StatusPill>
          <StatusDot aria-hidden="true" />
          <span>Under construction</span>
        </StatusPill>
      </TopBar>

      <Main maxWidth="lg">
        <Box>
          <Label>We're rebuilding</Label>
          <Title variant="h1">
            Our new site is on the way.
          </Title>
          <Lead>
            We're sharpening the experience, refreshing our case studies, and
            polishing everything end-to-end. Smart IT Innovation is still
            shipping software for clients — just reach out and we'll talk.
          </Lead>

          <PrimaryButton
            href="mailto:info@smartitinnovation.com"
            startIcon={<EmailOutlinedIcon sx={{ fontSize: 18 }} />}
            endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
            disableElevation
          >
            info@smartitinnovation.com
          </PrimaryButton>

          <InfoRow>
            <Box>
              <InfoLabel>Email</InfoLabel>
              <InfoValue>info@smartitinnovation.com</InfoValue>
            </Box>
            <Box>
              <InfoLabel>Phone</InfoLabel>
              <InfoValue>+92 (340) 710-1829</InfoValue>
            </Box>
            <Box>
              <InfoLabel>Offices</InfoLabel>
              <InfoValue>Ontario, CA · Lahore, PK</InfoValue>
            </Box>
          </InfoRow>
        </Box>
      </Main>

      <Footer>
        <span>© {new Date().getFullYear()} Smart IT Innovation</span>
        <span>Back online soon</span>
      </Footer>
    </Page>
  );
};

export default UnderConstruction;
